/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
export const msg91ValidationMessages = {
    firstName: 'Please enter a valid first name.',
    lastName: 'Please enter a valid last name.',
    mobileNumber: 'Please enter 10 digit mobile no.',
    email: 'Please enter valid email address.',
    pincode: 'Please enter a valid pincode.',
    emailInUse: 'Email address already in use.',
    mobileInUse: 'Mobile number already in use.',
    invalidOtp: 'Invalid OTP. Please double-check the code and try again.',
    otpLimit: 'You have reached the limit to resend OTP. Please try with a different mobile number or email.',
} as const;

const NAME_PATTERN = /^(?:(?:Mr\.|Mrs\.|Dr\.|Sri\.|Smt\.|Ms\.|M\/s\.)\s)?[A-Za-z]+(?:['\-\u2018\u2019\s]?[A-Za-z]+)*\.?$/i;
const INVALID_EMAIL_LOCAL_PARTS = new Set([
    'nomail',
    'noemail',
    'no',
    'na',
    'nomailid',
    'noemailid',
    'nil',
    'dummymail',
    'test',
    'yourname',
    'user',
    'user123',
    'randomuser',
    'demo',
    'example',
    '12345',
    'fakeemail',
    'placeholder',
]);
const INVALID_EMAIL_DOMAINS = new Set(['email', 'gamil', 'gamail', 'gemal', 'gimel']);
const INVALID_EMAIL_EXTENSIONS = new Set(['con', 'comn', 'comnn']);

export function isValidMsg91Name(value: string): boolean {
    return NAME_PATTERN.test(value.trim());
}

export function isValidMsg91MobileNumber(value: string): boolean {
    const digits = value.replace(/\D/g, '').replace(/^91(?=[6-9]\d{9}$)/, '');
    return /^(?!([0-9])\1{9})[6-9]\d{9}$/.test(digits);
}

export function isValidMsg91Email(value: string): boolean {
    const email = value.trim().toLowerCase();
    const match = /^([a-z0-9.!#$%&'*+/=?^_`{|}~-]+)@([a-z0-9-]+(?:\.[a-z0-9-]+)+)$/i.exec(email);
    if (!match) return false;

    const [, localPart, domain] = match;
    const domainParts = domain.split('.');
    const topLevelDomain = domainParts.at(-1);
    return !(
        INVALID_EMAIL_LOCAL_PARTS.has(localPart) ||
        INVALID_EMAIL_DOMAINS.has(domainParts[0]) ||
        !topLevelDomain ||
        INVALID_EMAIL_EXTENSIONS.has(topLevelDomain)
    );
}

export function isValidMsg91Pincode(value: string): boolean {
    return /^[1-9]\d{5}$/.test(value);
}

export function isMsg91OtpLimitError(message: string | null | undefined): boolean {
    return Boolean(message && /limit|exceed|too many|maximum|max attempts/i.test(message));
}
